/**
 * Asset-workflow exports (1.11): .xlsx (wells/DCA/EUR, ARIES compare, type curves,
 * review audit, Sources & provenance) and a .docx DCA/EUR memo. Zero-dep OOXML via
 * the same builders as the underwriting pack.
 */
import { xlsxParts } from '../engine/underwritingXlsx.js';
import { docxParts } from '../engine/capitalDocx.js';
import { downloadBytes } from '../engine/ooxmlZip.js';

const { buildWorkbook, headerRow, S, XLSX_MIME } = xlsxParts;
const r1 = (v, d = 1) => (v == null || !Number.isFinite(Number(v)) ? null : +Number(v).toFixed(d));
const mm = (v) => r1(v != null ? v / 1000 : null, 1); // Mcf → MMcf, or bbl → Mbbl
const eurUnit = (wf) => (wf?.product === 'oil' ? 'Mbbl' : 'MMcf');

export function workflowSheets(wf, { reviews = {}, audit = [], selection = null, freeze = null } = {}) {
  const vol = eurUnit(wf);
  const wells = (wf.wells || []).filter((w) => !selection || selection.includes(w.assetId));
  const label = wf.meta?.label || wf.dealId;
  const cover = [
    { values: [`D2D Energy · ${label}`], styles: [S.brandTitle] },
    { values: ['Core asset workflow · DCA / EUR / type curves'], styles: [S.brandSub] },
    [],
    { values: ['Confidential', wf.meta?.confidentiality || wf.confidentiality || 'CONFIDENTIAL'], styles: [S.label] },
    { values: ['Deal', wf.dealId], styles: [S.label] },
    { values: ['Workflow generated', wf.generatedAt], styles: [S.label] },
    { values: ['Wells in export', wells.length], styles: [S.label] },
    { values: ['Approved forecasts', Object.values(reviews).filter((r) => r.status === 'approved').length], styles: [S.label] },
    { values: ['Forecast freeze', freeze ? `${freeze.contentSha256} (${freeze.approvedCount} wells)` : 'none'], styles: [S.label] },
    { values: ['Units', `rate ${wf.units?.rate}; volumes ${wf.units?.volume}; EUR in ${vol}`], styles: [S.label] },
    [],
    { values: ['Method', wf.dcaMethod?.selection || ''], styles: [S.label, S.muted] },
    { values: ['Window', wf.dcaMethod?.window || ''], styles: [S.label, S.muted] },
    { values: ['Terminal decline', `${wf.dcaMethod?.terminalDeclineEffPct}%/yr effective`], styles: [S.label, S.muted] },
    { values: ['Economic limit', `${wf.dcaMethod?.economicLimit} ${wf.units?.rate}`], styles: [S.label, S.muted] },
    { values: ['Prepared by', 'Data2Dollars.ai'], styles: [S.label] },
  ];
  const dcaRows = [
    headerRow([
      'Asset ID', 'Well', 'API-10', 'Res cat', 'Status', 'WI', 'NRI', 'Model', 'qi (rate/d)', 'Di eff %',
      'b', 'Dmin eff %', 'R²', 'Holdout median APE %', 'Quality', 'q now', 'Decline now %', `Cum (${vol})`,
      `Remaining (${vol})`, `EUR (${vol})`, 'Flags', 'Review', 'Basis', 'Reviewer note',
    ]),
    ...wells.map((w) => {
      const f = w.fit || {};
      const rv = reviews[w.assetId] || {};
      return [
        w.assetId, w.name, w.api10, w.resCat, w.status, r1(w.wi, 4), r1(w.nri, 4), f.model || f.status,
        r1(f.qi, 2), r1(f.diEffPct, 2), r1(f.b, 2), r1(f.dminEffPct, 2), r1(f.r2, 3), r1(f.holdoutMapePct, 1),
        f.quality || '', r1(f.qNow, 1), r1(f.declineNowEffPct, 2), mm(w.cumToDate), mm(f.remaining), mm(f.eur),
        (f.flags || []).join('; '), rv.status || 'draft', rv.basis || '', rv.reason || '',
      ];
    }),
  ];
  const ariesRows = [
    headerRow(['Asset ID', 'Well', 'Seller start', 'Seller qi (rate/d)', 'Seller b', 'Seller Di (input %)', `Seller remaining (${vol})`, `D2D remaining (${vol})`, 'Δ remaining %', 'Seller qi vs q now %', 'Curve syntax']),
    ...wells
      .filter((w) => w.aries)
      .map((w) => [
        w.assetId, w.name, w.aries.start, r1(w.aries.arps?.qi, 2), r1(w.aries.arps?.b, 2), r1(w.aries.arps?.diInputPct, 2),
        mm(w.aries.remaining), mm(w.fit?.remaining), r1(w.compare?.remainingDeltaPct, 1), r1(w.compare?.qiAriesVsNowPct, 1),
        (w.aries.segments || []).join(' | '),
      ]),
  ];
  const tc = (wf.typeCurves || [])[0];
  const tcRows = tc
    ? [
        { values: [`Type curve · ${tc.group} · ${tc.wellCount} wells · ${tc.convention}`], styles: [S.brandSub] },
        headerRow(['Month', 'P10', 'P50', 'P90', 'Mean', 'Wells']),
        ...tc.curves.P50.map((_, k) => [k + 1, tc.curves.P10[k], tc.curves.P50[k], tc.curves.P90[k], tc.curves.mean[k], tc.curves.count[k]]),
        [],
        headerRow(['Case', 'Model', 'qi', 'Di (nominal/yr)', 'b', 'R²', `Type-curve EUR (${vol})`, `Well EUR distribution (${vol})`]),
        ...['P10', 'P50', 'P90'].map((k) => [
          k, tc.fits?.[k]?.model, tc.fits?.[k]?.qi, tc.fits?.[k]?.di, tc.fits?.[k]?.b, tc.fits?.[k]?.r2,
          mm(tc.eurTypeCurve?.[k]), mm(tc.eurWellDistribution?.[k]),
        ]),
      ]
    : [['No type curve (fewer than 3 wells with history)']];
  const auditRows = [
    headerRow(['When (UTC)', 'Asset ID', 'From', 'To', 'Basis', 'User', 'Reason', 'Params']),
    ...audit.map((a) => [a.created_at, a.asset_id, a.from_status || '', a.to_status, a.basis, a.user_name, a.reason || '', JSON.stringify(a.params)]),
  ];
  const prov = [
    headerRow(['Adapter', 'Source', 'SHA-256', 'Target', 'Rows']),
    ...(wf.provenance || []).map((p) => [p.adapter, p.source, p.sha256 || '', p.target, JSON.stringify(p.rows || {})]),
    [],
    headerRow(['Issue kind', 'Detail']),
    ...(wf.issues || []).map((i) => [i.kind, JSON.stringify(i)]),
    [],
    ['Trust rule', 'Every number traces to an adapter + source hash above. D2D fits are model output; the seller forecast case is the seller decline; approvals are desk judgment with audit.'],
  ];
  return [
    { name: 'Cover', rows: cover },
    { name: 'Wells DCA EUR', rows: dcaRows },
    { name: 'Seller vs D2D', rows: ariesRows },
    { name: 'Type curve', rows: tcRows },
    { name: 'Review audit', rows: auditRows },
    { name: 'Sources & provenance', rows: prov },
  ];
}

export function buildWorkflowXlsx(wf, opts) {
  return buildWorkbook(workflowSheets(wf, opts));
}

export function downloadWorkflowXlsx(wf, opts) {
  downloadBytes(`${wf.dealId}-asset-workflow.xlsx`, buildWorkflowXlsx(wf, opts), XLSX_MIME);
}

export function buildWorkflowDocx(wf, { reviews = {}, freeze = null } = {}) {
  const { p, h1, h2, brandBanner, table, footer, buildDocx } = docxParts;
  const s = wf.summary || {};
  const tc = (wf.typeCurves || [])[0];
  const approved = Object.entries(reviews).filter(([, r]) => r.status === 'approved');
  const top = [...(wf.wells || [])]
    .filter((w) => w.fit?.eur)
    .sort((a, b) => b.fit.remaining - a.fit.remaining)
    .slice(0, 12);
  const body = [
    brandBanner('DCA / EUR memo'),
    h1(`${wf.meta?.label || wf.dealId} — decline-curve review`),
    p(`Generated ${wf.generatedAt}. ${s.fitted}/${s.withHistory} wells with history fitted (quality A ${s.qualityCounts?.A}, B ${s.qualityCounts?.B}, C ${s.qualityCounts?.C}).`),
    p(`D2D remaining ${mm(s.remainingTotal)} ${eurUnit(wf)} vs seller remaining ${mm(s.ariesRemainingTotal)} ${eurUnit(wf)} (gross, all wells).`),
    p(`Approved forecasts: ${approved.length}. Freeze: ${freeze ? `${freeze.contentSha256.slice(0, 16)}… (${freeze.approvedCount} wells)` : 'not frozen'}.`),
    h2('Largest remaining volumes'),
    table(
      ['Well', 'Model', 'R²', `Remaining ${eurUnit(wf)}`, `Seller ${eurUnit(wf)}`, 'Review'],
      top.map((w) => [
        w.name, `${w.fit.model} b=${w.fit.b}`, String(w.fit.r2), String(mm(w.fit.remaining)),
        String(mm(w.aries?.remaining) ?? '—'), reviews[w.assetId]?.status || 'draft',
      ]),
    ),
    h2('Type curve'),
    tc
      ? p(`${tc.group}: ${tc.wellCount} wells. EUR P10 ${mm(tc.eurTypeCurve?.P10)} / P50 ${mm(tc.eurTypeCurve?.P50)} / P90 ${mm(tc.eurTypeCurve?.P90)} ${eurUnit(wf)} (${tc.convention}).`)
      : p('No type curve.'),
    h2('Method & provenance'),
    p(`${wf.dcaMethod?.selection}. Window: ${wf.dcaMethod?.window}. Terminal decline ${wf.dcaMethod?.terminalDeclineEffPct}%/yr.`),
    ...(wf.provenance || []).map((pv) => p(`${pv.adapter} · ${pv.source} · sha256 ${String(pv.sha256 || '').slice(0, 16)}… · ${JSON.stringify(pv.rows || {})}`, { size: 16 })),
    footer(),
  ].join('');
  return buildDocx(body);
}

export function downloadWorkflowDocx(wf, opts) {
  downloadBytes(`${wf.dealId}-dca-memo.docx`, buildWorkflowDocx(wf, opts), docxParts.DOCX_MIME);
}
